import * as yup from 'yup';
import moment from 'moment';

import { AuthorizedSignatorySchema } from 'services/authorizedSignatories';
import {
  validatePostalCode,
  stringRequired,
  validateState,
  validateCity,
} from 'utils/validation';

export interface SignatoryFileData {
  blobFileSize: number | null;
  blobFileName: string | null;
  blobFileExtension: string | null;
  file?: any;
}

export const defaultSignatoryFileData: SignatoryFileData = {
  blobFileSize: null,
  blobFileName: null,
  blobFileExtension: null,
  file: null,
};

export const getSignatoryDetailsSchema = (t) =>
  yup
    .object()
    .shape({
      givenName1: stringRequired(t, 'common:fields.firstname.label'),
      surnameFirst: stringRequired(t, 'common:fields.lastname.label'),
      dateOfBirth: yup
        .date()
        .typeError(
          t('dashboard:signatory.details.dateOfBirth.validation.valid'),
        )
        .test(
          'dateOfBirth',
          t('dashboard:signatory.details.dateOfBirth.validation.valid'),
          (value) => {
            const years = moment().diff(moment(value), 'years');
            return years > 16 && years < 120;
          },
        )
        .required(
          t('dashboard:signatory.details.dateOfBirth.validation.required'),
        ),
      nationalityCountryIsocode: stringRequired(
        t,
        'dashboard:signatory.details.nationality.label',
      ),
      idnumber: stringRequired(
        t,
        'dashboard:signatory.details.governmentId.label',
      ),
      countryIsocode: stringRequired(t, 'common:fields.country.label'),
      addressLine1: stringRequired(t, 'common:fields.address.label'),
      addressLine2: yup.string().trim().notRequired(),
      city: validateCity(t),
      stateOrProvince: validateState(t),
      postalCode: validatePostalCode(t),
    })
    .required();

export const mapApiToForm = (
  saved: AuthorizedSignatorySchema | null,
  defaultCountry: string,
  person?: { givenName1?: string; surnameFirst?: string },
) => ({
  givenName1: saved?.givenName1 || person?.givenName1 || '',
  surnameFirst: saved?.surnameFirst || person?.surnameFirst || '',
  // moment parses date-only strings as LOCAL midnight (new Date() would
  // parse them as UTC and shift the day in western timezones)
  dateOfBirth: saved?.dateOfBirth ? moment(saved.dateOfBirth).toDate() : null,
  // nationality follows the Ownership tab convention: a country ISO code
  // defaulting to the business country
  nationalityCountryIsocode: saved?.nationalityCountryIsocode || defaultCountry,
  idnumber: saved?.idnumber || '',
  countryIsocode: saved?.countryIsocode || defaultCountry,
  addressLine1: saved?.addressLine1 || '',
  addressLine2: saved?.addressLine2 ? saved.addressLine2.trim() : '',
  city: saved?.city || '',
  stateOrProvince: saved?.stateOrProvince || '',
  postalCode: saved?.postalCode || '',
});

export const getComparableValues = (values) => ({
  givenName1: (values.givenName1 || '').trim(),
  surnameFirst: (values.surnameFirst || '').trim(),
  dateOfBirth: values.dateOfBirth
    ? moment(values.dateOfBirth).format('YYYY-MM-DD')
    : null,
  nationalityCountryIsocode: (values.nationalityCountryIsocode || '').trim(),
  idnumber: (values.idnumber || '').trim(),
  countryIsocode: values.countryIsocode || '',
  addressLine1: (values.addressLine1 || '').trim(),
  addressLine2: (values.addressLine2 || '').trim(),
  city: (values.city || '').trim(),
  stateOrProvince: (values.stateOrProvince || '').trim(),
  postalCode: (values.postalCode || '').trim(),
});

export const getMappedSaveSignatoryFields = ({
  data,
  fileData,
  person,
  business,
  personDocumentsTypesId,
}) => {
  const fields: any = {
    businessesId: business.businessesId,
    personsId: person.personsId,
    modifiedBy: person.emailAddress,
    givenName1: data.givenName1,
    surnameFirst: data.surnameFirst,
    dateOfBirth: data.dateOfBirth,
    nationalityCountryIsocode: data.nationalityCountryIsocode,
    idnumber: data.idnumber,
    personDocumentsTypesId,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    city: data.city,
    stateOrProvince: data.stateOrProvince,
    countryIsocode: data.countryIsocode,
    postalCode: data.postalCode,
  };

  if (fileData?.file) {
    fields.blobFileSize = fileData.blobFileSize;
    fields.blobFileName = fileData.blobFileName;
    fields.blobFileExtension = fileData.blobFileExtension;
    fields.governmentIdFile = fileData.file;
  }

  return fields;
};
